import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../auth.service';
import { MarketingSessionService } from '../../analytics/marketing-session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { CheckCircle, Loader2, LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-auth-successful',
  standalone: true,
  imports: [CommonModule, RouterLink, LucideAngularModule],
  templateUrl: './auth-successful.html',
  styleUrl: './auth-successful.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthSuccessfulComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly marketingSessionService = inject(MarketingSessionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly icons = {
    CheckCircle,
    Loader2,
  };

  ngOnInit(): void {
    this.handleAuthRedirect();
  }

  private handleAuthRedirect(): void {
    this.route.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => {
          const token = params['token'];

          if (!token) {
            console.error('No token found in URL');
            return of(false);
          }

          return this.authService.validateToken(token);
        }),
      )
      .subscribe({
        next: (isValid) => {
          if (isValid) {
            // Link marketing session to user after OAuth
            this.marketingSessionService.linkToCurrentUser();
            // Token is valid, determine where to redirect
            this.redirectAfterAuth();
          } else {
            // Invalid token, redirect to landing page
            this.router.navigate(['/']);
          }
        },
        error: (error) => {
          console.error('Error during authentication:', error);
          this.router.navigate(['/']);
        },
      });
  }

  private redirectAfterAuth(): void {
    const params = this.route.snapshot.queryParams;
    const isNewUser = params['isNewUser'] === 'true';

    if (isNewUser) {
      this.marketingSessionService.trackEvent('CompleteRegistration');

      // New user - redirect to the signup flow destination (wizard step)
      const redirectUrl = params['redirect'] || '/';
      this.router.navigate([redirectUrl]);
    } else {
      // Existing user - redirect to dashboard
      this.router.navigate(['/dashboard']);
    }
  }
}
